<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="integrations-page">
    <div class="page-header">
      <div class="page-avatar"><v-icon size="22" color="#fff">mdi-api</v-icon></div>
      <div class="page-header-text">
        <h1 class="page-title">Integracje API</h1>
        <div class="page-subtitle">Połączenia z Bolt / Uber / FreeNow w jednym miejscu</div>
      </div>
    </div>

    <v-card outlined class="rounded-lg" style="overflow: hidden;">
      <v-tabs v-model="activeProvider" background-color="transparent" grow>
        <v-tab v-if="canAccess('bolt')">
          <v-icon left small>mdi-flash</v-icon>
          Bolt
          <v-icon
            v-if="bolt.lastTestStatus === 'FAIL'"
            size="10"
            color="error"
            class="ml-2"
          >mdi-circle</v-icon>
        </v-tab>
        <v-tab v-if="canAccess('uber')">
          <v-icon left small>mdi-car-sports</v-icon>
          Uber
          <v-icon
            v-if="uber.lastTestStatus === 'FAIL'"
            size="10"
            color="error"
            class="ml-2"
          >mdi-circle</v-icon>
        </v-tab>
        <v-tab v-if="canAccess('freenow')">
          <v-icon left small>mdi-cab</v-icon>
          FreeNow
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-tabs-items v-model="activeProvider" touchless>
        <!-- ============ BOLT ============ -->
        <v-tab-item v-if="canAccess('bolt')">
          <ProviderTabBody
            provider="Bolt"
            :status="bolt"
            :loading="bolt.loading"
            @save="saveCredentials('bolt')"
            @test="testConnection('bolt')"
            @remove="removeCredentials('bolt')"
            @toggle-auto-import="toggleAutoImport('bolt', $event)"
            @refresh="loadStatus('bolt')"
            @refresh-jobs="loadJobs('bolt')"
            @manual-run="manualRun('bolt')"
          >
            <template v-slot:related>
              <!-- Klucz Bolt Fleet API 2.0 (29.10.2026): kierowcy online na mapie.
                   Osobny od klucza v1 — stary klucz v2 odrzuca (498811). -->
              <div class="bolt-v2 px-4 py-3">
                <div class="d-flex align-center flex-wrap" style="gap: 8px">
                  <strong>Bolt Fleet API 2.0 — kierowcy online na mapie</strong>
                  <v-chip
                    x-small
                    :color="boltV2.configured ? 'success' : 'grey'"
                    text-color="white"
                    >{{ boltV2.configured ? 'Skonfigurowany ' + boltV2.masked : 'Brak klucza' }}</v-chip
                  >
                </div>
                <div class="caption grey--text mt-1">
                  Osobny klucz z panelu floty Bolta (Ustawienia → API, wersja 2.0). Bez niego mapa
                  dostaje pozycje z Bolta tylko przy zmianie statusu kierowcy.
                </div>
                <v-row dense class="mt-2">
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-model="boltV2.clientId"
                      label="Client ID (v2)"
                      outlined
                      dense
                      hide-details
                      autocomplete="off"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-text-field
                      v-model="boltV2.clientSecret"
                      label="Client Secret (v2)"
                      type="password"
                      outlined
                      dense
                      hide-details
                      autocomplete="new-password"
                    />
                  </v-col>
                  <v-col cols="12" md="2" class="d-flex align-center">
                    <v-btn
                      small
                      color="primary"
                      :disabled="!boltV2.clientId || !boltV2.clientSecret || !bolt.configured"
                      :loading="boltV2.saving"
                      @click="saveBoltV2"
                      >Zapisz</v-btn
                    >
                  </v-col>
                </v-row>
                <div class="d-flex align-center flex-wrap mt-2" style="gap: 8px">
                  <v-btn small outlined :disabled="!boltV2.configured" :loading="boltV2.testing" @click="testBoltV2"
                    >Test połączenia v2</v-btn
                  >
                  <v-btn small text color="error" :disabled="!boltV2.configured" @click="removeBoltV2">Usuń klucz v2</v-btn>
                  <span v-if="boltV2.result" :class="boltV2.result.ok ? 'success--text' : 'error--text'" class="caption">{{
                    boltV2.result.ok
                      ? 'Połączenie OK — aktywnych firm Bolta: ' + boltV2.result.activeCompanies
                      : 'Błąd: ' + boltV2.result.message
                  }}</span>
                </div>
              </div>
              <v-divider />
              <v-list dense color="transparent">
                <v-list-item link :to="'/bolt-sync-drivers'">
                  <v-list-item-icon>
                    <v-icon color="primary">mdi-sync</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Synchronizacja kierowców (Bolt UUID)</v-list-item-title>
                    <v-list-item-subtitle>Dopasowanie UUID kierowców z Bolta do naszej bazy</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon small>mdi-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </template>
          </ProviderTabBody>
        </v-tab-item>

        <!-- ============ UBER ============ -->
        <v-tab-item v-if="canAccess('uber')">
          <ProviderTabBody
            provider="Uber"
            :status="uber"
            :loading="uber.loading"
            @save="saveCredentials('uber')"
            @test="testConnection('uber')"
            @remove="removeCredentials('uber')"
            @toggle-auto-import="toggleAutoImport('uber', $event)"
            @refresh="loadStatus('uber')"
            @refresh-jobs="loadJobs('uber')"
            @manual-run="manualRun('uber')"
          >
            <template v-slot:related>
              <div class="caption grey--text px-4 py-3">
                Powiązane narzędzia — wkrótce (Vehicle Suppliers reports).
              </div>
            </template>
          </ProviderTabBody>
        </v-tab-item>

        <!-- ============ FREENOW ============ -->
        <v-tab-item v-if="canAccess('freenow')">
          <div class="pa-6 text-center grey--text">
            <v-icon size="48" color="#6E7691">mdi-cab</v-icon>
            <div class="mt-3 text-h6">FreeNow — wkrótce</div>
            <div class="caption mt-1">Integracja API z FreeNow nie jest jeszcze podpięta.<br>Daj znać gdy będziesz potrzebował.</div>
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Swal from "sweetalert2";
// Komponent wyciągnięty do osobnego SFC bo Vue 2 runtime-only build NIE
// kompiluje template-stringów w przeglądarce — inline `template: \`...\``
// dawało pusty tab. SFC kompiluje webpack przy buildzie.
import ProviderTabBody from "@/components/integrations/ProviderTabBody.vue";
import { canAccessSubtab } from "@/services/permissions";
const axios = require("axios");


export default {
  name: "IntegrationsPage",
  components: { ProviderTabBody },
  data() {
    return {
      activeProvider: 0,
      bolt: this.newStatusModel(),
      uber: this.newStatusModel(),
      boltV2: { configured: false, masked: '', clientId: '', clientSecret: '', saving: false, testing: false, result: null },
    };
  },
  async created() {
    await Promise.all([
      this.loadStatus('bolt'),
      this.loadStatus('uber'),
      this.loadJobs('bolt'),
      this.loadJobs('uber'),
      this.loadEquations(),
    ]);
  },
  methods: {
    canAccess(sub) { return canAccessSubtab('integrations', sub); },
    newStatusModel() {
      // Domyślny zakres dla ręcznego importu = poprzedni tydzień ISO (Pn–Nd)
      const now = new Date();
      const day = now.getDay() || 7; // 1=Mon..7=Sun
      const thisMon = new Date(now);
      thisMon.setDate(now.getDate() - day + 1);
      const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7);
      const lastSun = new Date(thisMon); lastSun.setDate(thisMon.getDate() - 1);
      const iso = (d) => d.toISOString().slice(0, 10);
      return {
        configured: false,
        clientId: '',
        clientIdMasked: '',
        clientSecret: '',
        externalCompanyId: '',
        lastTestedAt: null,
        lastTestStatus: null,
        autoImportEnabled: false,
        saving: false,
        testing: false,
        removing: false,
        loading: false,
        // Lista ostatnich uruchomień crona
        jobs: [],
        jobsLoading: false,
        // Ręczny import
        manualFrom: iso(lastMon),
        manualTo: iso(lastSun),
        manualEquationId: null,
        manualRunning: false,
        equations: [],
      };
    },
    authHeaders() {
      return { Authorization: 'Bearer ' + localStorage.getItem('token') };
    },
    apiUrl(p) { return process.env.VUE_APP_ROOT_API + p; },

    async loadStatus(provider) {
      const model = this[provider];
      model.loading = true;
      try {
        const res = await axios.get(this.apiUrl(`${provider}/status`), { headers: this.authHeaders() });
        const data = res.data?.data || {};
        Object.assign(model, {
          configured: !!data.configured,
          clientIdMasked: data.clientIdMasked || '',
          externalCompanyId: data.externalCompanyId || '',
          lastTestedAt: data.lastTestedAt || null,
          lastTestStatus: data.lastTestStatus || null,
          autoImportEnabled: !!data.autoImportEnabled,
          clientId: '',
          clientSecret: '',
        });
        if (provider === 'bolt')
          Object.assign(this.boltV2, { configured: !!data.v2Configured, masked: data.v2ClientIdMasked || '' });
      } catch (e) {
        console.error(`loadStatus ${provider}:`, e);
      } finally {
        model.loading = false;
      }
    },

    async saveCredentials(provider) {
      const model = this[provider];
      model.saving = true;
      try {
        await axios.post(
          this.apiUrl(`${provider}/credentials`),
          { clientId: model.clientId, clientSecret: model.clientSecret },
          { headers: this.authHeaders() },
        );
        Swal.fire({ icon: 'success', title: 'Zapisano', timer: 1200, showConfirmButton: false });
        await this.loadStatus(provider);
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać', 'error');
      } finally {
        model.saving = false;
      }
    },

    async testConnection(provider) {
      const model = this[provider];
      model.testing = true;
      try {
        await axios.post(this.apiUrl(`${provider}/test-connection`), {}, { headers: this.authHeaders() });
        Swal.fire({ icon: 'success', title: 'Test OK', timer: 1500, showConfirmButton: false });
        await this.loadStatus(provider);
      } catch (e) {
        Swal.fire('Test zawiódł', e.response?.data?.message?.toString() || 'Nieznany błąd', 'error');
        await this.loadStatus(provider);
      } finally {
        model.testing = false;
      }
    },

    async saveBoltV2() {
      this.boltV2.saving = true;
      try {
        await axios.post(
          this.apiUrl('bolt/credentials-v2'),
          { clientId: this.boltV2.clientId, clientSecret: this.boltV2.clientSecret },
          { headers: this.authHeaders() },
        );
        Object.assign(this.boltV2, { clientId: '', clientSecret: '', result: null });
        await this.loadStatus('bolt');
        await this.testBoltV2();
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać', 'error');
      } finally {
        this.boltV2.saving = false;
      }
    },

    async testBoltV2() {
      this.boltV2.testing = true;
      try {
        const res = await axios.post(this.apiUrl('bolt/test-connection-v2'), {}, { headers: this.authHeaders() });
        this.boltV2.result = res.data?.data || { ok: false, message: 'Brak odpowiedzi' };
      } catch (e) {
        this.boltV2.result = { ok: false, message: e.response?.data?.message?.toString() || 'Nieznany błąd' };
      } finally {
        this.boltV2.testing = false;
      }
    },

    async removeBoltV2() {
      const ok = await Swal.fire({
        title: 'Usunąć klucz Bolt Fleet API 2.0?',
        text: 'Mapa wróci do pozycji z Bolta tylko przy zmianie statusu kierowcy.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      try {
        await axios.delete(this.apiUrl('bolt/credentials-v2'), { headers: this.authHeaders() });
        this.boltV2.result = null;
        await this.loadStatus('bolt');
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się usunąć', 'error');
      }
    },

    async removeCredentials(provider) {
      const ok = await Swal.fire({
        title: `Usunąć dane API ${provider}?`,
        text: 'Stracisz dostęp do auto-importu i ręcznych operacji aż do ponownego skonfigurowania.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Tak, usuń',
        cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      const model = this[provider];
      model.removing = true;
      try {
        await axios.delete(this.apiUrl(`${provider}/credentials`), { headers: this.authHeaders() });
        await this.loadStatus(provider);
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się usunąć', 'error');
      } finally {
        model.removing = false;
      }
    },

    async toggleAutoImport(provider, enabled) {
      const model = this[provider];
      try {
        await axios.post(
          this.apiUrl(`${provider}/weekly/auto-import`),
          { enabled: !!enabled },
          { headers: this.authHeaders() },
        );
        model.autoImportEnabled = !!enabled;
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zmienić ustawienia', 'error');
        await this.loadStatus(provider);
      }
    },

    // Pobierz ostatnie 10 jobów typu ${PROVIDER}_WEEKLY_RUNNING z BackgroundJob
    async loadJobs(provider) {
      const model = this[provider];
      model.jobsLoading = true;
      try {
        const type = `${provider.toUpperCase()}_WEEKLY_RUNNING`;
        const res = await axios.get(
          this.apiUrl(`background-jobs?type=${type}`),
          { headers: this.authHeaders() },
        );
        // controller zwraca raw array (bez wrappera {data})
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        model.jobs = list;
      } catch (e) {
        console.error(`loadJobs ${provider}:`, e);
        model.jobs = [];
      } finally {
        model.jobsLoading = false;
      }
    },

    // Pobierz wzory MathEqs (filtrowane per provider) — dla selectora przy ręcznym imporcie.
    async loadEquations() {
      try {
        const res = await axios.get(
          this.apiUrl('import/math'),
          { headers: this.authHeaders() },
        );
        const all = res.data?.data || [];
        this.bolt.equations = all.filter(e => e.type === 'BOLT').map(e => ({ id: e.id, name: e.name }));
        this.uber.equations = all.filter(e => e.type === 'UBER').map(e => ({ id: e.id, name: e.name }));
        // Auto-wybierz pierwszy jeśli tylko jeden
        if (!this.bolt.manualEquationId && this.bolt.equations.length === 1) {
          this.bolt.manualEquationId = this.bolt.equations[0].id;
        }
        if (!this.uber.manualEquationId && this.uber.equations.length === 1) {
          this.uber.manualEquationId = this.uber.equations[0].id;
        }
      } catch (e) {
        console.error('loadEquations:', e);
      }
    },

    async manualRun(provider) {
      const model = this[provider];
      if (!model.configured) {
        Swal.fire('Błąd', `Najpierw skonfiguruj dane dostępu API ${provider}.`, 'warning');
        return;
      }
      const ok = await Swal.fire({
        title: `Uruchomić import ${provider}?`,
        html: `Okres: <b>${model.manualFrom}</b> → <b>${model.manualTo}</b><br>Wzór: <b>${(model.equations.find(e => e.id === model.manualEquationId) || {}).name || '?'}</b>`,
        icon: 'question', showCancelButton: true, confirmButtonText: 'Tak, uruchom', cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      model.manualRunning = true;
      try {
        await axios.post(
          this.apiUrl(`${provider}/weekly/run`),
          { from: model.manualFrom, to: model.manualTo, equationId: model.manualEquationId },
          { headers: this.authHeaders() },
        );
        Swal.fire({ icon: 'success', title: 'Import uruchomiony', text: 'Sprawdź "Historia importu" za chwilę.', timer: 2500, showConfirmButton: false });
        await this.loadJobs(provider);
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Import się nie powiódł', 'error');
      } finally {
        model.manualRunning = false;
      }
    },
  },
};
</script>

<style scoped>
.integrations-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.page-header { display: flex; align-items: center; gap: 12px; padding: 4px 4px 18px; }
.page-avatar {
  width: 44px; height: 44px; border-radius: 14px;
  background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 18px rgba(41,98,255,0.4);
}
.page-title { font-size: 22px; font-weight: 800; color: #fff; margin: 2px 0 0; letter-spacing: -0.02em; }
.page-subtitle { color: #6E7691; font-size: 12.5px; margin-top: 2px; }

.integrations-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255,255,255,0.06) !important;
  border-radius: 14px !important;
  color: #fff !important;
}
.integrations-page >>> .v-tab { color: #B0BEC5 !important; text-transform: none !important; font-weight: 600 !important; }
.integrations-page >>> .v-tab--active { color: #5478FF !important; }
.integrations-page >>> .v-tabs-slider { background: #5478FF !important; }
.integrations-page >>> .v-tabs-bar { background: transparent !important; }
.integrations-page >>> .v-text-field--outlined fieldset { border-color: rgba(255,255,255,0.1) !important; }
.integrations-page >>> .v-text-field--outlined .v-input__slot { background: #181C28 !important; border-radius: 10px !important; }
.integrations-page >>> .v-label { color: #6E7691 !important; }
.integrations-page >>> .v-text-field input { color: #fff !important; }
.integrations-page >>> .v-list-item__title { color: #fff !important; }
.integrations-page >>> .v-list-item__subtitle { color: #6E7691 !important; }
.integrations-page >>> .v-btn:not(.v-btn--icon) { text-transform: none !important; border-radius: 10px !important; font-weight: 600 !important; }
.integrations-page >>> .v-chip { border-radius: 999px !important; font-weight: 700 !important; }
.integrations-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
</style>
